/**
 * Palette — TypeScript mirror of CSS custom properties in src/styles/tokens.css.
 * Use these constants when you need hex values in JS/TSX (e.g. GSAP tweens,
 * canvas drawing, SVG fills). For declarative styling, prefer var(--ink) etc.
 */

export const palette = {
  // Base (the neutral spine)
  ink: "#050505",
  bone: "#f5f0e4",
  oil: "#1a1814",
  fog: "#8c847a",

  // Spot accents — one per section, never combined
  blood: "#b83319",
  flame: "#e86a1d",
  gold: "#f2c14e",
  loden: "#5b8b5a",
  vladimir: "#d8427a",
  cobalt: "#2e5c8a",
} as const;

export type PaletteToken = keyof typeof palette;
export type PaletteHex = (typeof palette)[PaletteToken];

/**
 * Section-to-spot-color mapping. Every section commits to ONE spot accent.
 * Sections 01 and 09 have no spot color (pure ink + bone).
 */
export const sectionSpot: Record<SectionId, PaletteToken | null> = {
  s01: null,
  s02: "blood",
  s03: "blood", // Act I — The Muscle
  s04: "vladimir", // Act II — The Owner (neon nightlife)
  s05: "gold", // Act III — The Business Man
  s05b: "gold", // The Films — Odyssey map
  s06: "cobalt", // Food Noir aesthetic
  s06b: "loden", // One Language — design DNA
  s07: "gold", // White Truffle label
  s08: "flame", // Brand Universe / lifestyle ecosystem
  s09: null, // End card
  s10: "flame", // La Famiglia crew
  s11: "loden", // The Canon gallery
};

export const sectionBackground: Record<SectionId, PaletteToken> = {
  s01: "ink",
  s02: "bone",
  s03: "ink",
  s04: "ink",
  s05: "ink", // was bone — now backed by HF#7 plantation photo
  s05b: "ink",
  s06: "ink",
  s06b: "ink",
  s07: "oil",
  s08: "ink",
  s09: "ink",
  s10: "ink",
  s11: "oil",
};

export type SectionId =
  | "s01"
  | "s02"
  | "s03"
  | "s04"
  | "s05"
  | "s05b"
  | "s06"
  | "s06b"
  | "s07"
  | "s08"
  | "s09"
  | "s10"
  | "s11";

export const sectionIds: readonly SectionId[] = [
  "s01",
  "s02",
  "s03",
  "s04",
  "s05",
  "s05b", // The Films — Odyssey map (after the three acts)
  "s06",
  "s06b", // One Language — design DNA (after Food Noir)
  "s07",
  "s08",
  "s10", // La Famiglia crew
  "s11", // The Canon gallery
  "s09", // End Card — always last
] as const;
